/**
 * buildManifest() — Fetch manifest and files from Rails, verify integrity, assemble manifest object
 *
 * This function performs the pre-flight checks for OTA updates:
 * 1. Fetches the manifest (manifest.json) from the server for a specific device type
 * 2. For each file in the manifest:
 *    - Downloads the file bytes
 *    - Computes SHA-256 hash
 *    - Verifies against the manifest entry
 * 3. Aborts if any file fails verification
 * 4. Returns a manifest object ready for runFirmwareUpdate()
 *
 * Manifest structure returned:
 * {
 *   version: "v0.5.0",
 *   releaseNotes: "<h2>...</h2><p>...</p>",   // aggregated across all skipped versions as HTML, or null
 *   files: [
 *     { path: "code.py", data: Uint8Array, sha256: "..." },
 *     { path: "www/index.html.gz", data: Uint8Array, sha256: "..." }
 *   ]
 * }
 */

import { marked } from 'marked';

const DEVICE_TYPE = 'midulate'; // Target device type for this web editor

/**
 * Computes SHA-256 hash of a Uint8Array using Web Crypto API
 * @param {Uint8Array} data - The data to hash
 * @returns {Promise<string>} - Hex string of the SHA-256 digest
 */
async function computeSha256(data) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Fetches a file from the Rails server and returns its bytes
 * @param {string} version - Firmware version (e.g., "v0.5.0")
 * @param {string} filePath - Path to the file (e.g., "code.py")
 * @returns {Promise<Uint8Array>} - File bytes
 * @throws {Error} - If fetch fails or status is not 200
 */
async function fetchFile(version, filePath) {
  const url = `/firmware/${DEVICE_TYPE}/${version}/files/${filePath}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${filePath}: ${response.status} ${response.statusText}`);
  }
  const buffer = await response.arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * Fetches the versions list from the Rails server
 * @returns {Promise<Object>} - The versions object: { device, latest, versions: [...] }
 */
async function fetchVersions() {
  const url = `/firmware/${DEVICE_TYPE}/versions.json`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch versions: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * Compares two semver strings. Returns negative if a < b, positive if a > b, 0 if equal.
 * Strips a leading 'v' if present.
 */
function compareSemver(a, b) {
  const parse = (v) => v.replace(/^v/, '').split('.').map(Number);
  const [aMaj, aMin, aPatch] = parse(a);
  const [bMaj, bMin, bPatch] = parse(b);
  return (aMaj - bMaj) || (aMin - bMin) || (aPatch - bPatch);
}

/**
 * Aggregates release notes for all versions strictly after currentVersion up to and
 * including targetVersion, in descending order (newest first).
 *
 * @param {Array<Object>} versions - The versions array from versions.json (any order)
 * @param {string|null} currentVersion - The device's current firmware version, or null
 * @param {string} targetVersion - The version being installed
 * @returns {string|null} - Concatenated release notes as HTML, or null if none available
 */
function aggregateReleaseNotes(versions, currentVersion, targetVersion) {
  const relevant = versions
    .filter(v => {
      if (!v.release_notes) return false;
      const afterCurrent = currentVersion ? compareSemver(v.version, currentVersion) > 0 : true;
      const upToTarget   = compareSemver(v.version, targetVersion) <= 0;
      return afterCurrent && upToTarget;
    })
    .sort((a, b) => compareSemver(b.version, a.version)); // newest first

  if (relevant.length === 0) return null;

  return relevant
    .map(v => marked.parse(`## ${v.version}\n\n${v.release_notes.trim()}`))
    .join('');
}

/**
 * Fetches the manifest from the Rails server
 * @param {string} version - Firmware version, or "latest" to fetch the most recent version
 * @param {Object} versionsData - Already-fetched versions data (to avoid a second fetch)
 * @returns {Promise<Object>} - The manifest object: { device, version, build_date, files: [...] }
 * @throws {Error} - If fetch fails or status is not 200
 */
async function fetchManifest(version, versionsData) {
  const targetVersion = version === 'latest' ? versionsData.latest : version;
  if (!targetVersion) {
    throw new Error('No firmware versions available');
  }

  const url = `/firmware/${DEVICE_TYPE}/${targetVersion}/manifest.json`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch manifest: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * Builds a complete manifest object for runFirmwareUpdate()
 *
 * This function:
 * 1. Fetches versions.json to resolve the target version and gather release notes
 * 2. Fetches the version manifest
 * 3. Downloads each file
 * 4. Verifies file integrity against the manifest's SHA-256 digests
 * 5. Returns a manifest with data, sha256, and aggregated release notes
 *
 * @param {string} version - Firmware version to download, or "latest" for the most recent
 * @param {Function} onProgress - Optional callback for status updates: onProgress({ status, file, current, total })
 * @param {string|null} currentVersion - The device's current firmware version, for scoping release notes
 * @returns {Promise<Object>} - Manifest ready for runFirmwareUpdate()
 * @throws {Error} - If manifest fetch fails, file download fails, or SHA-256 verification fails
 */
export async function buildManifest(version = 'latest', onProgress = null, currentVersion = null) {
  try {
    onProgress?.({ status: 'fetching_manifest' });

    const versionsData = await fetchVersions();
    const serverManifest = await fetchManifest(version, versionsData);

    const actualVersion = serverManifest.version;
    const files = serverManifest.files || [];

    const releaseNotes = aggregateReleaseNotes(versionsData.versions || [], currentVersion, actualVersion);

    const builtFiles = [];
    for (let i = 0; i < files.length; i++) {
      const fileEntry = files[i];
      onProgress?.({ status: 'downloading_file', file: fileEntry.path, current: i + 1, total: files.length });

      try {
        // Fetch file bytes
        const fileData = await fetchFile(actualVersion, fileEntry.path);

        // Compute SHA-256
        const computedSha256 = await computeSha256(fileData);

        // Verify against manifest entry (if present)
        const expectedSha256 = fileEntry.sha256 ?? null;
        if (expectedSha256 !== null && computedSha256 !== expectedSha256) {
          throw new Error(
            `SHA-256 mismatch for ${fileEntry.path}: ` +
            `expected ${expectedSha256}, got ${computedSha256}`
          );
        }

        // Add file with data and sha256 attached
        builtFiles.push({
          path: fileEntry.path,
          data: fileData,
          sha256: computedSha256,
        });
      } catch (err) {
        throw new Error(`Failed to process ${fileEntry.path}: ${err.message}`);
      }
    }

    onProgress?.({ status: 'ready', version: actualVersion, fileCount: builtFiles.length });

    return {
      version: actualVersion,
      releaseNotes,
      files: builtFiles,
    };
  } catch (err) {
    onProgress?.({ status: 'error', error: err.message });
    throw err;
  }
}
